import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Tabs,
  Tab,
  Menu,
  Grid,
  Typography,
  Chip,
} from '@mui/material';
import {
  Memory,
  Cable,
  ElectricBolt,
  Sensors,
  Settings,
  Search as SearchIcon,
} from '@mui/icons-material';

const navigationItems = [
  {
    name: 'All Parts',
    icon: <SearchIcon />,
    path: '/parts',
    isCategory: false
  },
  {
    name: 'Semiconductors',
    icon: <Memory />,
    subcategories: ['Microcontrollers', 'Processors', 'Memory', 'Logic ICs', 'Analog ICs'],
    isCategory: true
  },
  {
    name: 'Passives',
    icon: <ElectricBolt />,
    subcategories: ['Resistors', 'Capacitors', 'Inductors', 'Crystals', 'Filters'],
    isCategory: true
  },
  {
    name: 'Connectors',
    icon: <Cable />,
    subcategories: ['Headers', 'USB', 'HDMI', 'RF Connectors', 'Power Connectors'],
    isCategory: true
  },
  {
    name: 'Sensors',
    icon: <Sensors />,
    subcategories: ['Temperature', 'Pressure', 'Motion', 'Light', 'Gas Sensors'],
    isCategory: true
  },
  {
    name: 'Mechanical',
    icon: <Settings />,
    subcategories: ['Switches', 'Relays', 'Fuses', 'Heat Sinks', 'Enclosures'],
    isCategory: true
  },
];

const Navigation = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoveredCategory, setHoveredCategory] = useState(null);

  const handleCategoryHover = (event, categoryIndex) => {
    const item = navigationItems[categoryIndex];
    if (item.isCategory) {
      setAnchorEl(event.currentTarget);
      setHoveredCategory(categoryIndex);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setHoveredCategory(null);
  };

  const handleTabClick = (event, newValue) => {
    setSelectedCategory(newValue);
    const item = navigationItems[newValue];
    
    if (!item.isCategory && item.path) {
      navigate(item.path);
    }
  };

  const handleSubcategoryClick = (subcategory) => {
    navigate(`/parts?category=${encodeURIComponent(subcategory.toLowerCase())}`);
    handleMenuClose();
  };

  return (
    <Box sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
      <Container maxWidth="lg">
        <Tabs
          value={selectedCategory}
          onChange={handleTabClick}
          variant="scrollable"
          scrollButtons="auto"
        >
          {navigationItems.map((item, index) => (
            <Tab
              key={item.name}
              icon={item.icon}
              label={item.name}
              iconPosition="start"
              onMouseEnter={(e) => handleCategoryHover(e, index)}
              sx={{
                minHeight: 64,
                textTransform: 'none',
                fontWeight: 500,
                cursor: item.isCategory ? 'pointer' : 'pointer',
              }}
            />
          ))}
        </Tabs>
        
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl) && hoveredCategory !== null}
          onClose={handleMenuClose}
          MenuListProps={{
            onMouseLeave: handleMenuClose,
          }}
          PaperProps={{
            sx: { width: 600, mt: 1 }
          }}
        >
          {hoveredCategory !== null && navigationItems[hoveredCategory]?.isCategory && (
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {navigationItems[hoveredCategory]?.name}
              </Typography>
              <Grid container spacing={1}>
                {navigationItems[hoveredCategory]?.subcategories?.map((sub) => (
                  <Grid item key={sub}>
                    <Chip
                      label={sub}
                      variant="outlined"
                      clickable
                      size="small"
                      sx={{ mb: 1 }}
                      onClick={() => handleSubcategoryClick(sub)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Menu>
      </Container>
    </Box>
  );
};

export default Navigation;